import Image from 'next/image';

import { Box, Flex, Heading, Text, useToast, VisuallyHidden } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { ModalButton } from '~/components/modal-button';
import { NotifyMeForm } from '~/components/notify-me-form';
import { SanityImage } from '~/components/sanity-image';
import { trackEvent } from '~/features/analytics/track-event';
import { SanityImageType } from '~/features/page-template/models/image';
import { TemplateForm as TemplateFormType } from '~/features/page-template/models/template-form';

import { useFloatingShapes } from '../hooks/use-floating-shapes';
import { AvailableScholarshipList } from '../models/available-scholarships';

export type AvailableScholarshipsSectionProps = {
  title: string;
  scholarships: AvailableScholarshipList;
  notifyMeForm: TemplateFormType;
  programMap: Record<string, { title: string }> | null;
};

export function AvailableScholarshipsSection({
  title,
  scholarships,
  notifyMeForm,
  programMap,
}: AvailableScholarshipsSectionProps) {
  const toast = useToast();

  function renderProgramOptions(scholarship: AvailableScholarshipList[number]) {
    if (!scholarship.programsOffered?.length && !scholarship.forcedProgramsOfferedContent?.length) return null;
    else {
      return (
        <Box>
          <Heading as='p' color='blue.500' size='h6'>
            Program options
          </Heading>
          <Text>
            {scholarship?.forcedProgramsOfferedContent?.map(({ title }) => title).join(', ') ??
              scholarship?.programsOffered
                ?.map(({ key }) => programMap?.[key]?.title)
                .filter(Boolean)
                .join(', ')}
          </Text>
        </Box>
      );
    }
  }

  return (
    <Box
      as='section'
      bgColor='accent.sky.100'
      id='_available-scholarships-section'
      tabIndex={-1}
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <ContentContainer>
        <Heading
          as='h2'
          size='marketing-5xl'
          textAlign='center'
          marginBottom={{
            base: '24px',
            md: '48px',
          }}
        >
          {title}
        </Heading>
        <Flex flexDir='column' gap='24px'>
          {scholarships?.length > 0 ? (
            scholarships.map((scholarship, idx) => (
              <Card
                key={scholarship._id}
                as='article'
                boxShadow='lg'
                overflow='hidden'
                padding={{
                  base: '40px 16px 80px',
                  md: '40px 64px',
                }}
              >
                <Flex
                  alignItems='center'
                  gap={{ base: '72px', md: '128px' }}
                  flexDir={{
                    base: 'column',
                    md: 'row',
                  }}
                >
                  <Flex flex={{ md: '0 1 480px' }} flexDir='column' gap='40px'>
                    <Heading as='h3' size='marketing-4xl'>
                      {scholarship.title}
                    </Heading>
                    <Flex flexDir='column' gap='32px'>
                      {scholarship.whoCanApply && (
                        <Box>
                          <Heading as='p' color='blue.500' size='h6'>
                            Who can apply?
                          </Heading>
                          <Text>{scholarship.whoCanApply}</Text>
                        </Box>
                      )}
                      {renderProgramOptions(scholarship)}
                    </Flex>
                    <ButtonLink
                      buttonProps={{
                        ['aria-label']: `Learn more about the ${scholarship.title}`,
                        colorScheme: 'blue',
                        size: 'icon-right',
                        rightIcon: <ArrowRightIcon height='24px' width='24px' />,
                        width: {
                          base: '100%',
                          md: 'fit-content',
                        },
                        onClick: () => {
                          trackEvent({
                            name: 'Scholarship Page CTA Clicked',
                            type: 'click',
                            label: 'learn_more_clicked',
                            value: scholarship.title,
                          });
                        },
                      }}
                      linkProps={{
                        href: `/scholarships/${scholarship.slug}`,
                      }}
                    >
                      Learn More
                    </ButtonLink>
                  </Flex>
                  <SponsorCard
                    backgroundTheme={scholarship.theme}
                    companyName={scholarship.company.name}
                    idx={idx}
                    logo={scholarship.company.logo}
                  />
                </Flex>
              </Card>
            ))
          ) : (
            <Card
              as='article'
              boxShadow='lg'
              overflow='hidden'
              position='relative'
              padding={{
                base: '40px 16px',
                md: '100px 64px',
              }}
            >
              <Box
                position='absolute'
                right='0px'
                top='0px'
                display={{
                  base: 'none',
                  md: 'block',
                }}
              >
                <Box height='448px' overflow='hidden' position='relative' width='734px'>
                  <Image
                    alt=''
                    src='/images/backgrounds/background-lines-purple-lime.svg'
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'top',
                      pointerEvents: 'none',
                    }}
                    fill
                  />
                </Box>
              </Box>
              <Flex flexDir='column' gap='40px' maxWidth={{ md: '480px' }} position='relative'>
                <Heading as='p' size='marketing-4xl'>
                  No Current Scholarships
                </Heading>
                <Text>
                  There are no scholarships currently accepting applications. Please check back later, or sign up to be
                  notified about future scholarship programs.
                </Text>
                <ModalButton
                  buttonProps={{
                    size: 'icon-right',
                    variant: 'solid',
                    rightIcon: <ArrowRightIcon height='24px' width='24px' />,
                    width: {
                      base: '100%',
                      md: 'fit-content',
                    },
                    onClick: () =>
                      trackEvent({
                        name: 'Scholarship Page CTA Clicked',
                        type: 'click',
                        label: 'notify_me_clicked',
                        value: 'No Current Scholarships',
                      }),
                  }}
                  modalProps={{
                    title: 'Get notified for future scholarship opportunities.',
                    size: '3xl',
                    renderModalContent: ({ onClose }) => (
                      <NotifyMeForm
                        templateForm={notifyMeForm}
                        onCancelClick={onClose}
                        onError={() =>
                          toast({
                            variant: 'error',
                            title: 'Oops. Something went wrong. Please try again.',
                          })
                        }
                        onSubmit={() => {
                          toast({
                            title: 'Thank you for your interest!',
                            variant: 'success',
                          });
                          onClose();
                        }}
                      />
                    ),
                  }}
                >
                  Notify me of future programs
                </ModalButton>
              </Flex>
            </Card>
          )}
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function SponsorCard({
  idx,
  logo,
  companyName,
  backgroundTheme = 'light',
}: {
  idx: number;
  logo?: SanityImageType | null;
  companyName: string;
  backgroundTheme: 'light' | 'dark';
}) {
  const shapes = useFloatingShapes(idx);
  return (
    <Box
      borderRadius='24px 4px 4px 4px'
      flexShrink={0}
      marginX='auto'
      position='relative'
      height={{
        base: '222px',
        md: '268px',
      }}
      width={{
        base: '222px',
        md: '268px',
      }}
    >
      {shapes}
      <Card
        alignItems='center'
        bgColor={backgroundTheme === 'dark' ? 'blue.800' : 'gray.50'}
        border='none'
        borderRadius='24px 4px 4px 4px'
        boxShadow='lg'
        color={backgroundTheme === 'dark' ? 'white' : 'black'}
        display='flex'
        flexDir='column-reverse'
        gap='24px'
        height='100%'
        justifyContent='center'
        position='relative'
        width='100%'
      >
        {/* Note: we have a fall back image here in case there is no sponsor logo available */}
        {logo ? (
          <SanityImage alt='' image={logo} />
        ) : (
          <Image alt='' height={162} sizes='172px' src='/images/lime-medal.svg' width={136} />
        )}
        <Box textAlign='center'>
          {logo ? `Sponsored by ${companyName}` : <VisuallyHidden>Sponsored by {companyName}</VisuallyHidden>}
        </Box>
      </Card>
    </Box>
  );
}
